import { Card, Grid, Header } from '@mairistem/composants';
import { Icon, type UserRole } from '@mairistem/finances-utils';
import React from 'react';
import NextPeriode from '../../classes/outils/NextPeriode';
import PreviousPeriode from '../../classes/outils/PreviousPeriode';
import TypeBudget from '../../constants/simulation/typeBudget';
import TypePeriode from '../../constants/simulation/typePeriode';
import type Simulation from '../../interfaces/simulation/simulation';
import Outil from './Outils/Outil';
import SimulationParametresModal from './SimulationParametresModal';
import './SimulationAction.css';

type BaseAction = PreviousPeriode | NextPeriode;

interface SimulationActionProps {
	isDemandeEnabled: boolean;
	onRefresh: (onlyPanelData: boolean, typePeriode: TypePeriode) => void;
	onSave: (data: Simulation) => void;
	simulation: Simulation;
	userRole: UserRole;
}

const SimulationAction = ({
	isDemandeEnabled,
	onRefresh,
	onSave,
	simulation,
	userRole,
}: SimulationActionProps) => {
	const [openActionModal, setOpenActionModal] = React.useState<boolean>(false);
	const [openParametresModal, setOpenModalParametres] =
		React.useState<boolean>(false);
	const [selectedOutil, setSelectedOutil] = React.useState<BaseAction>();

	const outils = [
		new PreviousPeriode(simulation, isDemandeEnabled),
		new NextPeriode(simulation?.typePeriode),
	];

	const handleClickOutil = (outil: BaseAction) => {
		setSelectedOutil(outil);
		setOpenActionModal(true);
	};

	const handleClickParametres = () => {
		setOpenModalParametres(true);
	};

	const handleCloseActionModal = () => {
		setOpenActionModal(false);
	};

	const handleCloseParametresModal = () => {
		setOpenModalParametres(false);
	};

	const handleSaveParametresModal = (data: Simulation) => {
		onSave(data);
		setOpenModalParametres(false);
	};

	/**
	 * Fonction appele lors de la validation du changement d'une periode
	 * @param typePeriode Nouveau type de periode a mettre sur la page
	 */
	const handleValidate = (typePeriode: TypePeriode) => {
		onRefresh(false, typePeriode);
		setOpenActionModal(false);
	};

	const getPeriodeText = () => {
		let text = '';

		if (simulation?.transfertCpta) {
			switch (simulation?.budget?.type) {
				case TypeBudget.BUDGET_PRIMITIF:
					return 'BUDGET PRIMITIF RENDU EXECUTOIRE';
				case TypeBudget.BUDGET_SUPPLEMENTAIRE:
					return 'BUDGET SUPPLEMENTAIRE RENDU EXECUTOIRE';
				case TypeBudget.COMPTE_ADMINISTRATIF:
					return 'RESTES A REALISER VALIDES';
				case TypeBudget.DECISION_MODIFICATIVE:
					return `DM ${simulation.budget.dm.num} RENDU EXECUTOIRE`;
				default:
					return '';
			}
		}

		switch (simulation?.typePeriode) {
			case TypePeriode.DEMANDE:
				text = 'RECENSEMENT DES DEMANDES POUR';
				switch (simulation?.budget?.type) {
					case TypeBudget.BUDGET_PRIMITIF:
						return `${text} LE BUDGET PRIMITIF`;
					case TypeBudget.BUDGET_SUPPLEMENTAIRE:
						return `${text} LE BUDGET SUPPLEMENTAIRE`;
					case TypeBudget.DECISION_MODIFICATIVE:
						return `${text} LA DM N°${simulation.budget.dm.num}`;
					default:
						break;
				}
				break;
			case TypePeriode.PROPOSE:
				text = 'FORMULATION DES PROPOSITIONS';
				switch (simulation?.budget?.type) {
					case TypeBudget.BUDGET_PRIMITIF:
						return `${text} DU BUDGET PRIMITIF`;
					case TypeBudget.BUDGET_SUPPLEMENTAIRE:
						return `${text} DU BUDGET SUPPLEMENTAIRE`;
					case TypeBudget.DECISION_MODIFICATIVE:
						return `${text} DE LA DM ${simulation.budget.dm.num}`;
					default:
						break;
				}
				break;
			case TypePeriode.VOTE:
				text = 'VOTE ';
				switch (simulation?.budget?.type) {
					case TypeBudget.BUDGET_PRIMITIF:
						text += 'DU BUDGET PRIMITIF';
						break;
					case TypeBudget.BUDGET_SUPPLEMENTAIRE:
						text += 'DU BUDGET SUPPLEMENTAIRE';
						break;
					case TypeBudget.DECISION_MODIFICATIVE:
						text += `DE LA DM ${simulation.budget.dm.num}`;
						break;
					default:
						break;
				}

				return `${text} PAR L'ASSEMBLÉE`;
			case TypePeriode.CA_CFU:
				return 'SAISIE DES RESTES A REALISER';
			default:
				break;
		}

		return '';
	};

	const isCalendarShown = [
		TypeBudget.BUDGET_PRIMITIF,
		TypeBudget.BUDGET_SUPPLEMENTAIRE,
		TypeBudget.DECISION_MODIFICATIVE,
	].includes(simulation?.budget?.type);

	const renderSimulationText = (): React.JSX.Element => {
		const entiteLibelle = simulation?.exercice?.entite?.entite?.libelle;

		return (
			<>
				{entiteLibelle}
				<br />
				{`EXERCICE ${simulation?.exercice?.anneeExercice ?? ''}`}
				<br />
				<span className="periodeText">{getPeriodeText()}</span>
			</>
		);
	};

	return (
		<>
			<Card className="simulationInformations" fluid>
				<Card.Content>
					<Card.Header as={Header} size="tiny">
						<Grid>
							<Grid.Row>
								<Grid.Column width={isCalendarShown ? 13 : 16}>
									{renderSimulationText()}
								</Grid.Column>
								{isCalendarShown && (
									<Grid.Column textAlign="right" width={3}>
										<Icon
											cursor="pointer"
											iconSet="Lucide"
											name="CalendarRange"
											onClick={handleClickParametres}
										/>
									</Grid.Column>
								)}
							</Grid.Row>
						</Grid>
					</Card.Header>
					<Card.Description>
						<Grid padded>
							<Grid.Row className="nowrap">
								{outils?.map(
									(outil) =>
										outil?.isEnabled(userRole, simulation) && (
											<Grid.Column
												data-testid={`outil${outil?.code}`}
												onClick={() => handleClickOutil(outil)}
												width={outils.length === 1 ? 16 : 8}
											>
												<Outil icon={outil?.icon} title={outil?.title} />
											</Grid.Column>
										),
								)}
							</Grid.Row>
						</Grid>
					</Card.Description>
				</Card.Content>
			</Card>
			{selectedOutil?.renderModal(
				simulation,
				handleCloseActionModal,
				handleValidate,
				openActionModal,
			)}
			<SimulationParametresModal
				isDemandeEnabled={isDemandeEnabled}
				onClose={handleCloseParametresModal}
				onSave={handleSaveParametresModal}
				open={openParametresModal}
				simulation={simulation}
				userRole={userRole}
			/>
		</>
	);
};

export default SimulationAction;
